import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Grid, GridColumn, Notice, Separator, Text } from 'telia-front-react';
import FormErrorNotice from '../../components/FormErrorNotice';
import { useStores } from '../../stores/RootStore';
import { CustomerSegmentType } from '../../stores/SsoStore';
import CountryCallingCode from '../../forms/fields/CountryCallingCode';
import { scrollUpToElement } from '../../util/HtmlUtil';
import CustomerTypeSelector from '../common/fields/CustomerTypeSelector';
import DateAndTimeSelection from '../../forms/fields/DateAndTimeSelection';
import DescriptionField from '../../forms/fields/DescriptionField';
import EmailField from '../../forms/fields/EmailField';
import LanguageSelection from '../common/fields/LanguageSelection';
import NameField from '../common/fields/NameField';
import PhoneField from '../../forms/fields/PhoneField';
import RequestCallbackShowPreviewButton from './RequestCallbackShowPreviewButton';
import TopicSelection from '../common/fields/TopicSelection';

const RequestCallbackForm = observer((): React.JSX.Element => {
  const {
    callbackStore: {
      callbackCreation,
      updateReservationCreationBranchId,
      noDatesAvailable,
      requiredFieldsAreValid,
      hasCallbackCreationFormSubmitted,
      selectTime,
      dateAndTimeSelectionValid,
      getPhoneHelper,
      getEmailHelper,
      getNameHelper,
      getCallingCodeHelper,
      getDateAndTimeHelper,
      getTopicHelper,
      getAvailableReservationsForBranch,
    },
    countryCallingCodeStore: { isForeignCodeSelected },
    ssoStore,
  } = useStores();
  const { translate } = React.useContext(TranslationsContext);
  const { isMobileOrTablet } = useIsMobileHook();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    runInAction(() => (callbackCreation.customerId = ssoStore.customerId));
  }, [ssoStore.customerId]);

  React.useEffect(() => {
    if (noDatesAvailable) {
      scrollUpToElement('no-available-times-error-notice', isMobileOrTablet);
    }
  }, [noDatesAvailable]);

  const onCustomerTypeChange = (customerType: CustomerSegmentType) => {
    runInAction(() => (callbackCreation.subjectContext = customerType));
    updateReservationCreationBranchId();
  };

  return (
    <>
      <p>{translate('reservation.requestCallback.description')}</p>
      <Separator color="invisible" spacing="xsmall" />
      {noDatesAvailable && (
        <>
          <Notice id="no-available-times-error-notice" type="error" attributes={{ 'aria-live': 'assertive', 'aria-atomic': 'true' }}>
            {translate('reservation.noneAvailable')}
          </Notice>
          <Separator color="invisible" spacing="xsmall" />
        </>
      )}
      <Text>
        {hasCallbackCreationFormSubmitted && !requiredFieldsAreValid && (
          <FormErrorNotice
            erroredFields={[
              getTopicHelper(translate),
              getNameHelper(translate),
              getCallingCodeHelper(translate),
              getPhoneHelper(translate),
              isForeignCodeSelected ? getEmailHelper(translate) : undefined,
              getDateAndTimeHelper(translate),
            ]}
          />
        )}
        <CustomerTypeSelector customerType={callbackCreation.subjectContext} onChange={onCustomerTypeChange} />
        <TopicSelection />
        {!!callbackCreation.customerId && (
          <DescriptionField
            value={callbackCreation.description}
            onChange={(value: string) => runInAction(() => (callbackCreation.description = value))}
            label={translate('reservation.descriptionField')}
          />
        )}
        <NameField />
        <LanguageSelection />
        <Grid>
          <CountryCallingCode getHelper={() => getCallingCodeHelper(translate)} shouldValidate={hasCallbackCreationFormSubmitted} />
          <GridColumn width={['xs-12', 'sm-6', 'md-7']}>
            <PhoneField
              value={callbackCreation.phone}
              onChange={(value) => runInAction(() => (callbackCreation.phone = value))}
              getHelper={() => getPhoneHelper(translate)}
              shouldValidate={hasCallbackCreationFormSubmitted}
            />
          </GridColumn>
        </Grid>
        {isForeignCodeSelected && (
          <>
            <Notice size="small">
              <p className="text-light text-small">{translate('reservation.email.notice')}</p>
            </Notice>
            <EmailField
              value={callbackCreation.email}
              onChange={(value) => runInAction(() => (callbackCreation.email = value))}
              getHelper={() => getEmailHelper(translate)}
              shouldValidate={hasCallbackCreationFormSubmitted}
            />
          </>
        )}
        <DateAndTimeSelection
          label={translate('reservation.requestCallback.selectTime')}
          value={callbackCreation.time}
          onChange={selectTime}
          availableTimes={getAvailableReservationsForBranch(callbackCreation.branchId)}
          getHelper={() => getDateAndTimeHelper(translate)}
          shouldValidate={hasCallbackCreationFormSubmitted}
          isDisabled={!dateAndTimeSelectionValid}
        />
        <RequestCallbackShowPreviewButton />
      </Text>
    </>
  );
});

export default RequestCallbackForm;
